// In development the CRACO proxy forwards /api/* to the backend.
// In production set REACT_APP_API_BASE_URL to the deployed API origin.
const BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_BASE_URL ?? '')
  : '';

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface ApiError {
  message: string;
  status: number;
}

/* ── Token storage ── */
export const tokenStorage = {
  save(tokens: AuthTokens) {
    localStorage.setItem('pt360_access',  tokens.access);
    localStorage.setItem('pt360_refresh', tokens.refresh);
  },
  getAccess():  string | null { return localStorage.getItem('pt360_access');  },
  getRefresh(): string | null { return localStorage.getItem('pt360_refresh'); },
  clear() {
    localStorage.removeItem('pt360_access');
    localStorage.removeItem('pt360_refresh');
  },
};

/* ── Actual API response shape from /api/v1/onboard/auth/login/ ── */
interface LoginApiResponse {
  status:  number;
  message: string;
  data: {
    id:                          string;
    username:                    string;
    email:                       string;
    company_name:                string;
    industry_type:               string;
    company_registration_number: string;
    company_address:             string;
    country:                     string;
    state_province:              string;
    phone_number:                string;
    website:                     string;
    is_verified:                 boolean;
    is_active:                   boolean;
    date_joined:                 string;
    created_at:                  string;
    updated_at:                  string;
    access:                      string;
    refresh:                     string;
  };
}

export interface LoginResult {
  tokens: AuthTokens;
  user: LoginApiResponse['data'];
}

/* ── Login ── */
export async function loginWithCredentials(
  email: string,
  password: string,
): Promise<LoginResult> {
  const res = await fetch(`${BASE_URL}/api/v1/onboard/auth/login/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  });

  let body: LoginApiResponse | undefined;
  try { body = await res.json(); } catch { /* non-JSON error body */ }

  if (!res.ok) {
    const message =
      (body as any)?.detail ??
      (body as any)?.non_field_errors?.[0] ??
      (body as any)?.message ??
      'Invalid email or password.';
    throw { message, status: res.status } as ApiError;
  }

  const data = body!.data;
  const tokens: AuthTokens = { access: data.access, refresh: data.refresh };
  tokenStorage.save(tokens);

  return { tokens, user: data };
}

